import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS, CHART_COLORS_EMERALD } from "./reportesUtils";

applyPlugin(jsPDF);

const RecaudacionCodPres = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [recCodPresData, setRecCodPresData] = useState(null);
  const [recCodPresFechaDesde, setRecCodPresFechaDesde] = useState("");
  const [recCodPresFechaHasta, setRecCodPresFechaHasta] = useState("");
  const [recCodPresOficina, setRecCodPresOficina] = useState("todas");
  const [recCodPresFilters, setRecCodPresFilters] = useState({ codigo: "", partida: "", comprobantes: "", base: "", multas: "", descuento: "", total: "", porcentaje: "" });

  useEffect(() => { fetchRecCodPresData(); }, []);

  const fetchRecCodPresData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (recCodPresFechaDesde) params.append("fecha_desde", recCodPresFechaDesde);
      if (recCodPresFechaHasta) params.append("fecha_hasta", recCodPresFechaHasta);
      if (recCodPresOficina !== "todas") params.append("oficina", recCodPresOficina);
      const url = `${API}/reportes/recaudacion-por-codigo-presupuestario?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setRecCodPresData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de recaudación por código presupuestario");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosRecCodPres = () => {
    setRecCodPresFechaDesde("");
    setRecCodPresFechaHasta("");
    setRecCodPresOficina("todas");
    setRecCodPresFilters({ codigo: "", partida: "", comprobantes: "", base: "", multas: "", descuento: "", total: "", porcentaje: "" });
    setTimeout(() => fetchRecCodPresData(false), 0);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosRecCodPresActivos = recCodPresFechaDesde || recCodPresFechaHasta || recCodPresOficina !== "todas";
  const updateRecCodPresFilter = (key, value) => setRecCodPresFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosRecCodPresColActivos = Object.values(recCodPresFilters).some(v => v !== "");

  const filteredRecCodPresFilas = recCodPresData?.filas?.filter(f => {
    const pf = recCodPresFilters;
    if (pf.codigo && !(f.codigo_presupuestario || "").includes(pf.codigo)) return false;
    if (pf.partida && !(f.nombre_partida || "").toLowerCase().includes(pf.partida.toLowerCase())) return false;
    if (pf.comprobantes && !String(f.nro_comprobantes || 0).includes(pf.comprobantes)) return false;
    if (pf.base && !String(f.base_imponible || 0).includes(pf.base)) return false;
    if (pf.multas && !String(f.multas || 0).includes(pf.multas)) return false;
    if (pf.descuento && !String(f.descuento || 0).includes(pf.descuento)) return false;
    if (pf.total && !String(f.total_recaudado || 0).includes(pf.total)) return false;
    if (pf.porcentaje && !String(f.porcentaje || 0).includes(pf.porcentaje)) return false;
    return true;
  }) || [];

  const recCodPresTotalesFiltrados = filteredRecCodPresFilas.reduce((acc, f) => ({
    nro_comprobantes: acc.nro_comprobantes + (f.nro_comprobantes || 0),
    base_imponible: acc.base_imponible + (f.base_imponible || 0),
    multas: acc.multas + (f.multas || 0),
    descuento: acc.descuento + (f.descuento || 0),
    total_recaudado: acc.total_recaudado + (f.total_recaudado || 0),
  }), { nro_comprobantes: 0, base_imponible: 0, multas: 0, descuento: 0, total_recaudado: 0 });


  const exportRecCodPresExcel = () => {
    if (!recCodPresData) return;
    const wsData = [
      ["RECAUDACIÓN POR CÓDIGO PRESUPUESTARIO"], [],
      ["CÓDIGO", "NOMBRE PARTIDA", "NRO. COMP.", "BASE IMPONIBLE", "MULTAS", "DESCUENTO", "TOTAL RECAUDADO", "%"]
    ];
    filteredRecCodPresFilas.forEach(f => {
      wsData.push([f.codigo_presupuestario, f.nombre_partida, f.nro_comprobantes, f.base_imponible, f.multas, f.descuento, f.total_recaudado, f.porcentaje]);
    });
    wsData.push([]);
    wsData.push(["", "TOTALES", recCodPresTotalesFiltrados.nro_comprobantes, recCodPresTotalesFiltrados.base_imponible, recCodPresTotalesFiltrados.multas, recCodPresTotalesFiltrados.descuento, recCodPresTotalesFiltrados.total_recaudado, "100%"]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "RecCodPresupuestario");
    XLSX.writeFile(wb, `Recaudacion_Cod_Presupuestario_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportRecCodPresPDF = () => {
    if (!recCodPresData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(14);
    doc.setTextColor(5, 150, 105);
    doc.text("RECAUDACIÓN POR CÓDIGO PRESUPUESTARIO", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Total recaudado: Bs. ${recCodPresTotalesFiltrados.total_recaudado.toLocaleString("es-VE", {minimumFractionDigits: 2})}`, 14, 25);
    const headCols = ["Código", "Nombre Partida", "Comp.", "Base Imponible", "Multas", "Descuento", "Total Recaudado", "%"];
    const body = filteredRecCodPresFilas.map(f => [f.codigo_presupuestario, f.nombre_partida, f.nro_comprobantes, fmtBs(f.base_imponible), fmtBs(f.multas), fmtBs(f.descuento), fmtBs(f.total_recaudado), f.porcentaje + "%"]);
    body.push(["", "TOTALES", recCodPresTotalesFiltrados.nro_comprobantes, fmtBs(recCodPresTotalesFiltrados.base_imponible), fmtBs(recCodPresTotalesFiltrados.multas), fmtBs(recCodPresTotalesFiltrados.descuento), fmtBs(recCodPresTotalesFiltrados.total_recaudado), "100%"]);
    doc.autoTable({
      startY: 32, head: [headCols], body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [5, 150, 105], textColor: 255 },
      alternateRowStyles: { fillColor: [236, 253, 245] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [167, 243, 208]; } }
    });
    doc.save(`Recaudacion_Cod_Presupuestario_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportRecCodPresCSV = () => {
    if (!recCodPresData) return;
    const cols = ["CÓDIGO", "NOMBRE PARTIDA", "NRO. COMP.", "BASE IMPONIBLE", "MULTAS", "DESCUENTO", "TOTAL RECAUDADO", "%"];
    const rows = filteredRecCodPresFilas.map(f => [`"${f.codigo_presupuestario}"`, `"${f.nombre_partida}"`, f.nro_comprobantes, f.base_imponible, f.multas, f.descuento, f.total_recaudado, f.porcentaje].join(","));
    const blob = new Blob(["\ufeff" + cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Recaudacion_Cod_Presupuestario_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const oficinasDisponibles = recCodPresData?.resumen?.oficinas || [];
  const chartData = filteredRecCodPresFilas.map(f => ({
    nombre: f.nombre_partida.length > 25 ? f.nombre_partida.substring(0, 25) + "..." : f.nombre_partida,
    nombre_completo: f.nombre_partida,
    codigo: f.codigo_presupuestario,
    total: f.total_recaudado,
    porcentaje: f.porcentaje
  }));
  return (
    <div className="space-y-6" data-testid="reporte-recaudacion-codigo-presupuestario">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="reccod-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recaudación por Código Presupuestario
            </h1>
            <p className="text-slate-500">Ingresos recaudados agrupados por código presupuestario</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchRecCodPresData(false)} disabled={loading} data-testid="reccod-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!recCodPresData} data-testid="reccod-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportRecCodPresExcel} data-testid="reccod-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecCodPresPDF} data-testid="reccod-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecCodPresCSV} data-testid="reccod-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Desde</Label>
              <Input type="date" value={recCodPresFechaDesde} onChange={(e) => setRecCodPresFechaDesde(e.target.value)} className="bg-white" data-testid="reccod-fecha-desde" />
            </div>
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Hasta</Label>
              <Input type="date" value={recCodPresFechaHasta} onChange={(e) => setRecCodPresFechaHasta(e.target.value)} className="bg-white" data-testid="reccod-fecha-hasta" />
            </div>
            <div className="w-[200px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Oficina</Label>
              <Select value={recCodPresOficina} onValueChange={setRecCodPresOficina}>
                <SelectTrigger className="bg-white" data-testid="reccod-oficina">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {oficinasDisponibles.map(o => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchRecCodPresData(true)} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reccod-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosRecCodPresActivos && (
                <Button variant="outline" onClick={limpiarFiltrosRecCodPres} disabled={loading} data-testid="reccod-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      ) : recCodPresData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Recaudado</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="reccod-card-total">{fmtBs(recCodPresData.resumen?.total_recaudado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Comprobantes</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="reccod-card-comprobantes">{recCodPresData.resumen?.total_comprobantes || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Códigos Presupuestarios</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="reccod-card-codigos">{recCodPresData.resumen?.total_codigos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Promedio por Código</p>
                <p className="text-lg font-bold text-amber-600" data-testid="reccod-card-promedio">{fmtBs(recCodPresData.resumen?.promedio_por_codigo)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de barras horizontal */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Código Presupuestario</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: "100%", height: Math.max(chartData.length * 50 + 40, 200) }} data-testid="reccod-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tickFormatter={(val) => `Bs. ${(val / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="nombre" width={200} tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(val) => [`Bs. ${val.toLocaleString("es-VE", {minimumFractionDigits: 2})}`, "Recaudado"]}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.nombre_completo || label}
                      />
                      <Bar dataKey="total" name="Total Recaudado" radius={[0, 6, 6, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS_EMERALD[index % CHART_COLORS_EMERALD.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle por Código Presupuestario</CardTitle>
                  <CardDescription>{filteredRecCodPresFilas.length} de {recCodPresData.filas?.length || 0} códigos</CardDescription>
                </div>
                {hayFiltrosRecCodPresColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setRecCodPresFilters({ codigo: "", partida: "", comprobantes: "", base: "", multas: "", descuento: "", total: "", porcentaje: "" })} data-testid="reccod-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="reccod-tabla">
                  <thead>
                    <tr className="border-b-2 border-emerald-200 bg-emerald-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Código</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Nombre Partida</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Comp.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Base Imponible</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Multas</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Descuento</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">Total Recaudado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-emerald-800 uppercase">%</th>
                    </tr>
                    <tr className="bg-emerald-50/60 border-b border-emerald-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recCodPresFilters.codigo} onChange={(e) => updateRecCodPresFilter("codigo", e.target.value)} data-testid="reccod-filter-codigo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recCodPresFilters.partida} onChange={(e) => updateRecCodPresFilter("partida", e.target.value)} data-testid="reccod-filter-partida" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recCodPresFilters.comprobantes} onChange={(e) => updateRecCodPresFilter("comprobantes", e.target.value)} data-testid="reccod-filter-comprobantes" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recCodPresFilters.base} onChange={(e) => updateRecCodPresFilter("base", e.target.value)} data-testid="reccod-filter-base" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recCodPresFilters.multas} onChange={(e) => updateRecCodPresFilter("multas", e.target.value)} data-testid="reccod-filter-multas" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recCodPresFilters.descuento} onChange={(e) => updateRecCodPresFilter("descuento", e.target.value)} data-testid="reccod-filter-descuento" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recCodPresFilters.total} onChange={(e) => updateRecCodPresFilter("total", e.target.value)} data-testid="reccod-filter-total" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recCodPresFilters.porcentaje} onChange={(e) => updateRecCodPresFilter("porcentaje", e.target.value)} data-testid="reccod-filter-porcentaje" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecCodPresFilas.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-slate-400">No se encontraron datos de recaudación</td></tr>
                    ) : (
                      filteredRecCodPresFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-emerald-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 font-mono">{f.codigo_presupuestario}</span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-slate-800 font-medium">{f.nombre_partida}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{f.nro_comprobantes}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.base_imponible)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-orange-600">{fmtBs(f.multas)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-blue-600">{fmtBs(f.descuento)}</td>
                          <td className="py-2.5 px-3 text-sm text-right font-bold text-emerald-700">{fmtBs(f.total_recaudado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-slate-100 rounded-full h-2">
                                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${Math.min(f.porcentaje, 100)}%` }}></div>
                              </div>
                              <span className="text-xs font-semibold text-slate-600 w-10 text-right">{f.porcentaje}%</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredRecCodPresFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-emerald-100 border-t-2 border-emerald-300">
                        <td colSpan={2} className="py-3 px-3 text-sm font-bold text-emerald-900 uppercase">Totales ({filteredRecCodPresFilas.length} códigos)</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-emerald-900">{recCodPresTotalesFiltrados.nro_comprobantes}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-emerald-900">{fmtBs(recCodPresTotalesFiltrados.base_imponible)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-orange-700">{fmtBs(recCodPresTotalesFiltrados.multas)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-blue-700">{fmtBs(recCodPresTotalesFiltrados.descuento)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-emerald-900">{fmtBs(recCodPresTotalesFiltrados.total_recaudado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-emerald-900">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchRecCodPresData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default RecaudacionCodPres;
